import React, { useEffect, useState } from 'react';
import { Download, Upload, AlertTriangle } from 'lucide-react';
import toolkitService from '../../services/ToolkitService';
import ConfirmModal from '../shared/ConfirmModal';
import CopyButton from '../shared/CopyButton';
import { setManualHandler } from './useOnPrem';

// A request a server job issued and is waiting on (toolkit jobs.js, no SyncConnect): open this dialog on it, so its
// script can be run and its result uploaded — which tells the job to carry on. Resolves when uploaded.
let openHandler = null;
export const runIssuedScript = (id, ids) => (openHandler ? openHandler({ kind: 'issued', id, ids })
    : Promise.reject(new Error('The script dialog is not available on this page')));
import { STEP } from './ToolkitFileStep';

// "Run it yourself" — for a project with NO SyncConnect. When a screen asks the property's OPERA v5 database something
// (useOnPrem ask / write), this dialog opens instead: the server issues the exact script SyncConnect would have run
// (toolkit onprem.js script — recorded, and audited), the user runs it in SQL Developer, exports the result grid as
// CSV / Excel and uploads it here (onprem upload). The screen then carries on with that answer as if SyncConnect had
// sent it. Cancelling stops the screen's step with "no result uploaded".
//
// Mounted once per screen by ToolkitProjectHeader; registers itself as useOnPrem's manual handler.

const errorOf = (e) => e?.response?.data?.message || e?.message || String(e);

function ToolkitManualRun({ ctx }) {
    const [req, setReq] = useState(null);          // { request, resolve, reject }
    const [issued, setIssued] = useState(null);    // the script from the server
    const [state, setState] = useState({ state: STEP.IDLE, message: '' });

    useEffect(() => {
        const open = (request) => new Promise((resolve, reject) => {
            setIssued(null);
            setState({ state: STEP.BUSY, message: 'Preparing the script…' });
            setReq({ request, resolve, reject });
        });
        openHandler = open;
        const undo = setManualHandler(open);
        return () => { undo(); if (openHandler === open) openHandler = null; };
    }, []);

    // Issue the script as soon as the dialog opens: it is the record of what the user was asked to run.
    useEffect(() => {
        if (!req) return;
        const { request } = req;
        const [op, args] = request.kind === 'issued' ? ['script-of', { id: request.id }]
            : request.kind === 'write' ? ['script', { write: request.write, args: request.args, confirm: request.confirm }]
                : ['script', { key: request.key, params: request.params }];
        toolkitService.onprem(ctx, op, args)
            .then(({ data }) => { setIssued(data); setState({ state: STEP.IDLE, message: '' }); })
            .catch((e) => setState({ state: STEP.ERROR, message: errorOf(e) }));
    }, [req, ctx]);

    if (!req) return null;
    const isWrite = req.request.kind === 'write' || (req.request.kind === 'issued' && !!issued?.write);
    const close = (err) => { req.reject(err || new Error('Cancelled — no result was uploaded')); setReq(null); };

    const download = () => {
        const url = URL.createObjectURL(new Blob([issued.script], { type: 'text/plain' }));
        const a = document.createElement('a');
        a.href = url; a.download = issued.fileName; a.click();
        URL.revokeObjectURL(url);
    };

    const onFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        setState({ state: STEP.BUSY, message: `Uploading ${file.name}…` });
        try {
            const key = await toolkitService.upload(file, (pct) => setState({ state: STEP.BUSY, message: `Uploading ${file.name}… ${pct}%` }));
            setState({ state: STEP.BUSY, message: 'Reading the result…' });
            const { data } = await toolkitService.onprem(ctx, 'upload', { id: issued.id, key, ids: issued.ids || req.request.ids });
            req.resolve(data);
            setReq(null);
        } catch (err) {
            setState({ state: STEP.ERROR, message: errorOf(err) });
        }
    };

    return (
        <ConfirmModal wide closeOnBackdrop={false} hideConfirm cancelLabel="Cancel" onCancel={() => close()}
            title={isWrite ? 'Run this change yourself' : 'Run this query yourself'}
            message="This project has no SyncConnect, so the Toolkit cannot reach the property's OPERA v5 database. Run the script below in SQL Developer, then upload its result here.">
            {isWrite && (
                <div className="tkb-write-confirm" style={{ marginBottom: '0.6rem' }}>
                    <div className="tkb-write-head"><AlertTriangle size={16} /> This script changes the property's live OPERA v5 database</div>
                    <p className="tkb-msg" style={{ margin: '0.3rem 0 0' }}>Run it once, as given. Upload its result so the Toolkit can show — and the audit trail record — what it did.</p>
                </div>
            )}
            {issued ? (
                <>
                    <p className="tkb-msg"><strong>{issued.title}{issued.label ? ` — ${issued.label}` : ''}</strong> (request {issued.id})</p>
                    <ol className="tkb-msg" style={{ paddingLeft: '1.2rem', margin: '0.3rem 0 0.6rem' }}>
                        <li>Download or copy the script and open it in SQL Developer, connected to the property's v5 schema.</li>
                        <li>Run the {isWrite ? 'DECLARE / BEGIN' : 'BEGIN'} … END block (Ctrl+Enter) — give the v5 resort code when asked — then the SELECT (Ctrl+Enter).</li>
                        <li>Right-click the result grid › Export… as <strong>csv</strong> or <strong>excel</strong>, header on, and upload the file below.</li>
                    </ol>
                    <textarea readOnly value={issued.script} rows={10} style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.75rem' }} />
                    <div className="tkb-row" style={{ marginTop: '0.5rem' }}>
                        <button type="button" className="tkb-file" onClick={download}><Download size={15} /> Download script</button>
                        <CopyButton value={issued.script} label="Copy script" />
                        <label className={`tkb-file${state.state === STEP.BUSY ? ' tkb-file-disabled' : ''}`}>
                            <Upload size={15} /> Upload the result (CSV / Excel)
                            <input type="file" accept=".csv,.xlsx,.xls,.txt" onChange={onFile} hidden disabled={state.state === STEP.BUSY} />
                        </label>
                    </div>
                    <p className="tkb-hint">The script fits this request only. Do not edit the query or the exported file — the columns are read by name.</p>
                </>
            ) : null}
            {state.message && <p className={`tkb-msg${state.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{state.message}</p>}
        </ConfirmModal>
    );
}

export default ToolkitManualRun;
